import axios from 'axios';
import {withStore} from '../../../utils/memoize';
import {waitForLock} from '../../../utils/critical-section';
import { makeLogger } from '../../../logger/logger';
import type { SvsStatusColor, SvsStatusType } from '../../design-system/status/svs-status';
import type { GuidedApiQuickLink } from '../api';

const { err } = makeLogger({
  pathToCurrentFile: 'components/guided/subcomponents/utils.ts',
  loggersList: ['err']
});

/**
 * Set class for guided search item detecting preselectedLink and arrows movement
**/
export const selectCorrectClassForSelectedItem = (element: HTMLElement, preselectedLink: number) => {
    const listItems = element.querySelectorAll('.guided-pane_item');

    listItems.forEach((item, index) => {
        if (index === preselectedLink) {
          item.classList.add('guided-keyboard-selected');
        } else {
          item.classList.remove('guided-keyboard-selected');
        }
    });
};

/**
 * Select previous selected item again after reopening guided search
**/
export const selectCorrectPreselectedItem = (element: Element, preselectedLink: number, showGuidedPane: boolean, isAddOperation = true) => {
    if (element && showGuidedPane) {
      const preselectedItem = element?.querySelectorAll('.guided-pane_item')[preselectedLink];
      const itemClassList = preselectedItem?.classList;
      const classToChange = 'guided-keyboard-selected';

      isAddOperation ? itemClassList?.add(classToChange) : itemClassList?.remove(classToChange);
    }
};

/**
 * The function sorts data based on string that contains the order of sections in the view
**/
export const orderDataByPredefinedOrderingRules = (dataArray: GuidedApiQuickLink[], orderingString: string): GuidedApiQuickLink[] => {
    if (!dataArray.length) return [];
    if (dataArray.length === 1) return dataArray;

    const orderingArray = orderingString.split(',');

    dataArray.sort((a, b) => {
        const indexA = orderingArray.indexOf(a?.sectionId);
        const indexB = orderingArray.indexOf(b?.sectionId);

        return indexA - indexB;
    });

    return dataArray;
};

export function debounce(func, timeout) {
    let timer;
    return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            func.apply(this, args);
        }, timeout);
    };
}

export const PromiseTimeout = (promise, timeoutInMilliseconds, rejectOnTimeoutReason: unknown|string) => {
    return Promise.race([
      promise,
      new Promise(function(resolve, reject){
          setTimeout(function() {
              reject(rejectOnTimeoutReason || 'timeout');
          }, timeoutInMilliseconds);
      })
    ]);
};



export const _getToken = async ({tokenUrl, bearer})=>{
    //debugger;
    const apigeeParams = new URLSearchParams();
    apigeeParams.append('grant_type', 'client_credentials');

    const getTokenResponse = await axios.post(tokenUrl, apigeeParams, {
      headers: {
        'Authorization': `Basic ${bearer}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      }
    });

    if (getTokenResponse.status !== 200) {
      const msg = 'Failed to get bearer token! Response is not 200';

      err(msg);
      throw new Error(msg);
    }

    const json = getTokenResponse.data;

    if (json.status !== 'approved') {
      const msg = 'Failed to get bearer token, response is not success';

      err(msg);
      throw new Error(msg);
    }
    return json;
};

interface apigeeResponseTokenInterface{
    issued_at?,
    expires_in?,
    access_token?
}

async function getApigeeTokenWithMemoize(tokenUrl, bearer) {

    const safeDelta = 300; //we ask for new token ahead of time...


    const tokenArgs = {tokenUrl, bearer};
    //debugger;
    let apigeeTokenResponse = await withStore<apigeeResponseTokenInterface>({
      cbkArgs: tokenArgs,
      cbk: _getToken
    });


    //check the token age, if token is expired, we remove it from the store
    const {
      issued_at:__issued_at_str_apigee_probably_ms = '0',
      expires_in:__expires_in_str_apigee_s = '0'
    } = apigeeTokenResponse;
    /**
   * __What happens here__
   * Apigee returns values as strings in the JSON response.
   * `issued_at` is a string like "1708596568080" that's in miliseconds format, or "1708596568" that's in seconds format
   * while `expires_in` is in seconds
   * new Date().valueOf() returns in MS, we use it in `now`.
   * so we must convert `now` and `issued_at` from MS to S. Remembering that `issued_at` could be not in MS but in S
   * Around 20240220 apigee stopped using S format and started returning MS, causing token expiration errors
   * SVS-22840 bug has been created for the problem
   *
   * So - to solve the problem we try guessing the `issued_at` format. If we guess correctly, it should be working fine.
   * :-)
   */

    const issued_at = (()=>{
        if(__issued_at_str_apigee_probably_ms.length >= 11)
          return Math.ceil(parseInt(__issued_at_str_apigee_probably_ms) / 1000); //convert milisecond timestamp to regular
        else return parseInt(__issued_at_str_apigee_probably_ms);
    })();

    const expires_in = parseInt(__expires_in_str_apigee_s);


    const now = Math.ceil(new Date().valueOf() / 1000); //convert milisecond timestamp to regular


    if(issued_at + expires_in <= now + safeDelta){

      withStore.clear({
        args: tokenArgs
      });

      //overwrite the previous token as it's expired
      apigeeTokenResponse = await withStore({
        cbkArgs: tokenArgs,
        cbk: _getToken
      });

    }

    const {access_token = null} = apigeeTokenResponse;

    if(access_token == null){
      throw new Error('Apigee returned null token');
    }

    return {
      'Authorization': `Bearer ${access_token}`
    };
}

interface authenticationHeadersInterface{
    'X-IDMS-Authorization'?,
    Authorization?,
    'Content-Type'?,
    apikey?,
}

interface AuthenticationHeadersParams{
    apikey?:string,
    bearer?:string,
    idmsToken?:string,
    tokenUrl?:string
    apigeeBasicAuth?:string,
    liveLockInterval?:number,
    liveLockMaxDelay?:number
}

export const authenticationHeaders = async (args:AuthenticationHeadersParams):Promise<authenticationHeadersInterface>=>{

    const {
      apikey = null,
      bearer = null,
      tokenUrl = null,
      idmsToken = null,
      apigeeBasicAuth = null,
      liveLockInterval = 50,
      liveLockMaxDelay = 3000,
    } = args;

    return await waitForLock({
      interval: liveLockInterval,
      maxDelay: liveLockMaxDelay,
      executeAfterTimeout: true,
      cbk: async ()=>{
          //only apikey or nothing at all
          let r = {};

          if(apikey)
            r['apikey'] = apikey;


          if(idmsToken){
            r['X-IDMS-Authorization'] = idmsToken;
          }

          if(bearer){
            r['Authorization'] = `Bearer ${bearer}`;
          } else if(apigeeBasicAuth && tokenUrl){
            //add apigee headers to the return object
            r = {
              ...r,
              ...await getApigeeTokenWithMemoize(tokenUrl, apigeeBasicAuth)
            };
          }

          return r;
      }
    });

};

export const PromiseResolvers = {
  isRejected: (input: PromiseSettledResult<unknown>): input is PromiseRejectedResult =>
    input.status === 'rejected',
  isFulfilled: <T>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> =>
    input.status === 'fulfilled'
};

export const mapStatusToColor:(string)=>SvsStatusColor = (status) => {
    switch (status) {
    case 'Submitted':
      return 'yellow';
    case 'Open':
      return 'green';
    case 'In Process':
    case 'Partially Shipped':
    case 'Invoiced':
    case 'O':
      return 'orange';
    case 'CREATED':
    case 'E':
    case 'R':
      return 'grey';
    default:
      return 'green';
    }
};

export const mapStatusToIcon:(string)=>SvsStatusType = (status) => {
    switch (status) {
    case 'Completed':
      return 'completed-ellipse';
    case 'Cancelled':
    case 'CREATED':
    case 'E':
    case 'R':
      return 'cancelled-ellipse';
    case 'Pending modification':
      return 'warning-triangle';
    default:
      return 'ellipse';
    }
};
